import React, { useState } from "react";
import { faHandHoldingDollar } from "@fortawesome/free-solid-svg-icons";

import CollapsingPills from "$components/table/cells/collapsing-pills";
import LargeNumberRange from "$components/table/cells/large-number-range";
import LogoCell from "$components/table/cells/logo";
import PredefinedNumberRange from "$components/table/cells/predefined-number-range";
import ProbabilityBubble from "$components/probability";
import { getTerm } from "$stores/dictionary";
import { initiativesApi } from "../../api";

const DECISION_LABELS = { pursue: "Pursue", pass: "Pass", revisit: "Revisit" };
const DECISION_CLASSES = { pursue: "-primary", pass: "-secondary", revisit: "-secondary" };

const DecisionCell = ({ data, initiativeId, onDecision }) => {
    const [localDecision, setLocalDecision] = useState(data.decisionStatus);
    const [noteText, setNoteText] = useState(data.decisionNote || "");
    const [notesOpen, setNotesOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const save = async (status, note) => {
        setSaving(true);
        setSaveError(null);
        try {
            await initiativesApi.recordDecision(initiativeId, data.projectKey, status, note || null);
            onDecision(data.projectKey, status, note);
        } catch {
            setSaveError("Failed to save.");
            throw new Error();
        } finally {
            setSaving(false);
        }
    };

    const handleDecision = async (status) => {
        const prev = localDecision;
        setLocalDecision(status);
        try {
            await save(status, noteText);
        } catch {
            setLocalDecision(prev);
        }
    };

    const handleNoteSave = async () => {
        if (!localDecision) { setNotesOpen(false); return; }
        try {
            await save(localDecision, noteText);
        } catch { /* error shown inline */ }
        setNotesOpen(false);
    };

    return (
        <div className="decision-cell" onClick={(e) => e.stopPropagation()}>
            <div className="decision-cell__buttons">
                {["pursue", "pass", "revisit"].map((status) => (
                    <button
                        key={status}
                        className={`button -small${localDecision === status ? ` ${DECISION_CLASSES[status]}` : " -outline"}`}
                        disabled={saving}
                        onClick={() => handleDecision(status)}
                    >
                        {DECISION_LABELS[status]}
                    </button>
                ))}
                <button
                    className="button -small -text"
                    onClick={() => setNotesOpen(!notesOpen)}
                >
                    {notesOpen ? "Close" : noteText ? "Edit note" : "Note"}
                </button>
            </div>
            {saveError && <div className="decision-cell__error">{saveError}</div>}
            {notesOpen && (
                <div className="decision-cell__notes">
                    <textarea
                        className="decision-cell__textarea"
                        rows={2}
                        placeholder="Add a note..."
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                    />
                    <button
                        className="button -small -primary"
                        disabled={saving || !localDecision}
                        title={!localDecision ? "Record a decision first" : undefined}
                        onClick={handleNoteSave}
                    >
                        {saving ? "Saving..." : "Save"}
                    </button>
                </div>
            )}
        </div>
    );
};

export const buildDefaultColumns = ({ initiativeId, onDecision }) => [
    {
        Header: () => getTerm("projectType"),
        accessorKey: "name",
        id: "name",
        size: 120,
    },
    {
        Header: () => getTerm("parentClient"),
        accessorKey: "clientGroupName",
        id: "clientGroupName",
        Cell: ({ data, value }) => <LogoCell logoUrl={data?.clientLogoUrl} value={value} />,
    },
    {
        Header: () => getTerm("subClient"),
        accessorKey: "clientName",
        id: "clientName",
    },
    {
        Header: () => getTerm("serviceLine"),
        accessorKey: "serviceLine",
        id: "serviceLine",
        size: 120,
        enableSorting: false,
        Cell: ({ data, value }) => {
            const items = value ? [{ id: value, text: value }] : [];
            return <CollapsingPills data={items} maxVisible={2} keyPrefix="serviceLine" analyticsLabel={getTerm("serviceLines")} />;
        },
    },
    {
        Header: () => getTerm("industry"),
        accessorKey: "industries",
        id: "industries",
        size: 150,
        enableSorting: false,
        Cell: ({ data, value }) => {
            const pills = (value || []).map((i) => ({ id: i.id, text: i.text }));
            return <CollapsingPills data={pills} maxVisible={2} keyPrefix="industry" analyticsLabel={getTerm("industries")} />;
        },
    },
    {
        Header: () => getTerm("projectTypeGroup"),
        accessorKey: "practiceGroups",
        id: "practiceGroups",
        size: 150,
        enableSorting: false,
        Cell: ({ data, value }) => {
            const pills = (value || []).map((i) => ({ id: i.id, text: i.text }));
            return <CollapsingPills data={pills} maxVisible={2} keyPrefix="practiceGroup" analyticsLabel={getTerm("projectTypeGroups")} />;
        },
    },
    {
        Header: () => "Tags",
        accessorKey: "tags",
        id: "tags",
        size: 120,
        enableSorting: false,
        Cell: ({ data }) => {
            const tags = [];
            if (data.requiresIndependence) {
                tags.push({ id: "independence", text: "Check Conflicts", modifierClassNames: ["-independence"] });
            }
            if (data.isCrossSell) {
                tags.push({ id: "cross-sell", text: "Cross-Sell", icon: faHandHoldingDollar, modifierClassNames: ["-importance"] });
            }
            return <CollapsingPills data={tags} maxVisible={2} keyPrefix="tags" analyticsLabel="Tags" />;
        },
    },
    {
        Header: () => `${getTerm("subClient")} Size`,
        accessorKey: "clientSize",
        id: "clientSizeSortOrder",
        sortDescFirst: true,
        Cell: ({ data, value }) => <PredefinedNumberRange value={value} isHealthy={true} />,
    },
    {
        Header: () => getTerm("averageRevenue"),
        accessorKey: "averageRevenue",
        id: "averageRevenue",
        sortDescFirst: true,
        Cell: ({ data, value }) => <LargeNumberRange value={value} decimalPrecision={0} />,
    },
    {
        Header: () => "Probability",
        accessorKey: "probability",
        id: "probability",
        sortDescFirst: true,
        size: 100,
        Cell: ({ data, value }) => <ProbabilityBubble value={value} />,
    },
    {
        Header: () => "Partner",
        accessorKey: "crmName",
        id: "crmName",
        enableSorting: false,
    },
    {
        Header: () => "Decision",
        accessorKey: "decisionStatus",
        id: "decisionStatus",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ data }) => (
            <DecisionCell
                data={data}
                initiativeId={initiativeId}
                onDecision={onDecision}
            />
        ),
    },
];
